import { theme } from "antd";
import PageLayout from "../PageLayout";
import styles from "./Terms.module.css";

const { useToken } = theme;

const Section = ({ n, title, children }) => (
	<section className={styles.section}>
		<h2 className={styles.heading}>
			<span className={styles.num}>{n}</span>
			<span>{title}</span>
		</h2>
		{children}
	</section>
);

const Terms = () => {
	const { token } = useToken();

	// Feed theme tokens into the CSS module as variables — keeps all colors
	// token-driven while the layout/typography lives in Terms.module.css.
	const themeVars = {
		"--terms-text": token.colorText,
		"--terms-secondary": token.colorTextSecondary,
		"--terms-primary": token.colorPrimary,
		"--terms-border": token.colorBorderSecondary,
		"--terms-fill": token.colorFillTertiary,
	};

	return (
		<PageLayout>
			<div className={styles.page} style={themeVars}>
				<header className={styles.hero}>
					<p className={styles.eyebrow}>Legal</p>
					<h1 className={styles.title}>Terms of Service</h1>
					<p className={styles.subtitle}>
						The terms that govern your use of BodhiStreams. Please read them
						carefully — by using the Service you agree to them.
					</p>
					<span className={styles.updated}>Last updated: July 1, 2026</span>
				</header>

				<Section n="01" title="Acceptance of Terms">
					<p>
						These Terms of Service ("Terms") govern your access to and use of
						the BodhiStreams mobile application and website (bodhistreams.com)
						(together, the "Service"), operated by BodhiStreams ("we", "our", or
						"us"). By creating an account or using the Service, you agree to
						these Terms. If you do not agree, do not use the Service.
					</p>
				</Section>

				<Section n="02" title="The Service">
					<p>
						BodhiStreams is a music streaming service that lets you stream audio
						content, create playlists, and manage your listening across devices.
						We may add, change, or remove features at any time.
					</p>
				</Section>

				<Section n="03" title="Accounts">
					<ul className={styles.list}>
						<li>
							You must provide accurate information and keep it up to date. You
							are responsible for activity under your account.
						</li>
						<li>
							You are responsible for keeping your credentials secure, including
							any third-party sign-in (such as Google Sign-In) you use to access
							the Service.
						</li>
						<li>
							You must be old enough to form a binding contract in your
							jurisdiction.
						</li>
					</ul>
				</Section>

				<Section n="04" title="Subscriptions, Billing & Cancellation">
					<ul className={styles.list}>
						<li>
							Paid plans are billed on a recurring basis through our payment
							processor, Stripe. By subscribing you authorize recurring charges
							until you cancel.
						</li>
						<li>
							Subscriptions renew automatically at the end of each billing
							period unless cancelled beforehand.
						</li>
						<li>
							You may cancel at any time; your plan remains active until the end
							of the current billing period, after which it will not renew.
						</li>
						<li>
							Prices may change; we will give reasonable notice of changes
							before they take effect. Except where required by law, payments
							are non-refundable.
						</li>
					</ul>
				</Section>

				<Section n="05" title="Acceptable Use">
					<p>You agree not to:</p>
					<ul className={styles.list}>
						<li>
							Use the Service for any unlawful purpose or in violation of these
							Terms.
						</li>
						<li>
							Copy, download for redistribution, resell, or publicly perform
							content except as expressly permitted by the Service.
						</li>
						<li>
							Circumvent, disable, or interfere with security, access controls,
							or usage limits, or attempt to access accounts or data that are
							not yours.
						</li>
						<li>
							Scrape, reverse engineer, or use automated means to access the
							Service beyond what our interfaces permit.
						</li>
					</ul>
				</Section>

				<Section n="06" title="Content & Intellectual Property">
					<p>
						All content available through the Service, including audio, artwork,
						and software, is owned by BodhiStreams or its licensors and is
						protected by intellectual-property laws. We grant you a limited,
						non-exclusive, non-transferable, revocable licence to access the
						content for personal, non-commercial use through the Service. No
						other rights are granted.
					</p>
				</Section>

				<Section n="07" title="Termination & Account Deletion">
					<p>
						You may delete your account and associated data at any time — see{" "}
						<a href="/delete-account" className={styles.link}>
							bodhistreams.com/delete-account
						</a>
						. We may suspend or terminate your access if you violate these Terms
						or if required to protect the Service or other users.
					</p>
				</Section>

				<Section n="08" title="Disclaimers">
					<p>
						The Service is provided "as is" and "as available" without
						warranties of any kind, whether express or implied, including
						fitness for a particular purpose and non-infringement. We do not
						warrant that the Service will be uninterrupted, error-free, or that
						content will always be available.
					</p>
				</Section>

				<Section n="09" title="Limitation of Liability">
					<p>
						To the maximum extent permitted by law, BodhiStreams will not be
						liable for any indirect, incidental, special, consequential, or
						punitive damages, or for lost profits or data, arising from your use
						of the Service. Our total liability for any claim relating to the
						Service will not exceed the amount you paid us in the twelve months
						before the claim.
					</p>
				</Section>

				<Section n="10" title="Changes to These Terms">
					<p>
						We may update these Terms from time to time. We will post the
						updated Terms on this page and update the "Last updated" date.
						Continued use of the Service after changes take effect constitutes
						acceptance.
					</p>
				</Section>

				<p className={styles.footer}>
					Questions about these Terms? Contact us at{" "}
					<a href="mailto:support@bodhistreams.com" className={styles.link}>
						support@bodhistreams.com
					</a>
					.
				</p>
			</div>
		</PageLayout>
	);
};

export default Terms;
